import { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Grid,
  Box,
  TableBody,
  TableRow,
  TableCell,
  Toolbar,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

import {
  ButtonSubmit,
  ButtonCancel,
  Select,
  DateTimePicker,
} from "components/shared";
import { RowNoData } from "components/tables";
import { useTable, useSetEvents } from "hooks";
import { IOrder, IAddressee, ISetEvents } from "config/interfaces";
import {
  convertDateAndTime,
  convertDate,
  getDateISOBegin,
  getDateISOEnd,
} from "utils";
import { TableTitle } from "./styles";
import EasyTechIcons from "components/Icons/EasyTechIcons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      borderRadius: "4px",
      background: "var(--backgroundGray)",
      width: "100%",
      "& .MuiToolbar-regular": {
        padding: 0,
      },
    },
    table: {
      borderRadius: "4px",
      background: "#f8f8f8",
      width: "100%",
    },
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      maxWidth: "calc(100% - 12px)",
    },
    input: {
      "& .MuiInputBase-input": {
        boxShadow: "none",
        fontSize: "1.2rem",
        fontWeight: "500",
        textTransform: "uppercase",
      },
    },
    checkbox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "var(--orange)",
      },
      "& .MuiSvgIcon-root": {
        width: "1.4rem",
      },
    },
  })
);
interface TableProps {
  title?: string;
}

const filters: IOrder.SelectField[] = [
  {
    label: "PERÍODO",
    value: "period",
  },
];
interface HeadCell {
  disableSorting?: boolean;
  id: keyof ISetEvents.Table;
  label: string;
}

const headCells: HeadCell[] = [
  { id: "icon", label: "", disableSorting: true },
  { id: "date", label: "DATA" },
  { id: "action", label: "EVENTO" },
  { id: "type", label: "TIPO" },
  { id: "box", label: "BOX" },
  { id: "user", label: "USUÁRIO" },
];

interface DataState {
  filter: string | null;
  dateFrom: Date;
  dateTo: Date;
  dateOf?: string;
  status: string;
}

const initialState: DataState = {
  filter: null,
  dateFrom: new Date(),
  dateTo: new Date(),
  dateOf: "DHINICIO",
  status: "",
};

export default function TableRecipients({ title }: TableProps) {
  const [data, setData] = useState(initialState);
  const [rows, setRows] = useState([] as ISetEvents.SetEvents[]);
  const [selectedRows, setSelectedRows] = useState([] as Array<string>);

  const [filterFn] = useState({
    fn: (items: any) => {
      return items;
    },
  });
  const {
    loading,
    events: records,
    statusOptions,
    getEventsByFilters,
    setPagination,
    getAllEvents,
  } = useSetEvents();

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable({
      records,
      headCells,
      filterFn,
      config: { checkbox: false, actions: false },
      setPagination,
    });

  const classes = useStyles();

  useEffect(() => {
    setRows(records);
  }, [records]);

  const onSelectAll = useCallback((selectedRows: Array<string>) => {
    setSelectedRows(selectedRows);
  }, []);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id.toString());
      return onSelectAll(newSelecteds);
    }
    onSelectAll([] as Array<string>);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;

    setData({ ...data, [name]: value ? value : checked });
  };

  const handleDateChange = (value?: any, name?: string) => {
    if (name) {
      setData({ ...data, [name]: value });
    }
  };

  const handleFilterClick = () => {
    switch (data.filter) {
      case "period":
        let params: IAddressee.Params = {
          DHFIM_BEGIN: getDateISOBegin(data.dateFrom),
          DHFIM_END: getDateISOEnd(data.dateTo),
        };
        return getEventsByFilters(params);
      default:
        return;
    }
  };

  const handleCancelFilter = () => {
    setData({ ...data, filter: null });
    getAllEvents();
  };

  return (
    <>
      <Paper className={classes.toolbar} elevation={0}>
        <Toolbar>
          <Grid container spacing={2} justifyContent="flex-start">
            <Grid item xs={6} md={6} alignItems="flex-start">
              <Box display="flex" justifyContent="flex-start">
                <Select
                  label="FILTRO"
                  name="filter"
                  choices={filters}
                  value={data.filter}
                  onChange={handleChange}
                />
              </Box>
            </Grid>
          </Grid>
        </Toolbar>

        {data.filter && (
          <Toolbar id="toolbar-period" style={{ marginTop: "16px" }}>
            <Grid container spacing={2} justifyContent="flex-start">
              <Grid item xs={12} md={6} xl={4}>
                <Paper style={{ padding: "16px" }}>
                  {data.filter === "period" && (
                    <Grid container spacing={2} justifyContent="flex-start">
                      <Grid item xs={6}>
                        <DateTimePicker
                          label="DE: "
                          name="dateFrom"
                          format="dd/MM/yyyy"
                          value={data.dateFrom}
                          onChange={(e) => handleDateChange(e, "dateFrom")}
                        />
                      </Grid>
                      <Grid item xs={6} justifyContent="flex-start">
                        <DateTimePicker
                          label="ATÉ: "
                          name="dateTo"
                          format="dd/MM/yyyy"
                          value={data.dateTo}
                          minDate={data.dateFrom}
                          minDateMessage={`A data deve ser maior ou igual a ${convertDate(
                            String(data.dateFrom)
                          )}`}
                          onChange={(e) => handleDateChange(e, "dateTo")}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {data.filter === "status" && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Select
                          label="STATUS"
                          name="status"
                          choices={statusOptions}
                          value={data.status}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Box mt={3} display="flex" justifyContent="flex-end">
                    <ButtonCancel
                      onClick={handleCancelFilter}
                      style={{ marginRight: "16px" }}
                    >
                      CANCELAR
                    </ButtonCancel>
                    <ButtonSubmit autoFocus onClick={handleFilterClick}>
                      FILTRAR
                    </ButtonSubmit>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Toolbar>
        )}
      </Paper>

      {!loading ? (
        <Paper className={classes.table} elevation={0}>
          {title && <TableTitle>{title}</TableTitle>}

          <TblContainer>
            <TblHead
              numSelected={selectedRows.length}
              rowCount={rows.length}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {recordsAfterPagingAndSorting().length === 0 ? (
                <RowNoData />
              ) : (
                recordsAfterPagingAndSorting().map(
                  (row: ISetEvents.SetEvents, index: any) => {
                    return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell align="left" width="5%">
                          <EasyTechIcons name={row.code} />
                        </TableCell>
                        <TableCell align="left">
                          {convertDateAndTime(row.date)}
                        </TableCell>
                        <TableCell align="left">{row.action}</TableCell>
                        <TableCell align="left">{row.type}</TableCell>
                        <TableCell align="left">{row.box}</TableCell>
                        <TableCell align="left">{row.user}</TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </TblContainer>

          <TblPagination />
        </Paper>
      ) : (
        <h3>Loading...</h3>
      )}
    </>
  );
}
